import React, { useState, useEffect } from 'react';
import * as apiClient from '../services/apiClient';
import { 
  ChevronDown, 
  ChevronUp, 
  Gift, 
  Film, 
  Plane, 
  Sparkles, 
  Edit3, 
  ThumbsUp, 
  CheckCircle2, 
  Clock, 
  ShieldCheck, 
  Plus, 
  X, 
  AlertCircle,
  Trash2,
  RotateCcw
} from 'lucide-react';

/**
 * CardRewardsAccordion
 * Renders the 4-tier rewards matrix for a master credit card, with:
 * - Direct in-table 'Suggest Edit' capability (edit cells, add/delete rows, submit proposed table)
 * - Cardholder community proposals voting (without disclosing specific vote thresholds)
 */
export default function CardRewardsAccordion({
  masterCardId,
  cardName = 'Credit Card',
  isCardholder = false,
  showToast = () => {}
}) {
  const [sections, setSections] = useState([]);
  const [edits, setEdits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    cashback_spending: true,
    bogo_offers: false,
    lounge_travel: false,
    lifestyle_perks: false
  });
  const [activeTab, setActiveTab] = useState('tables'); // 'tables' or 'proposals'
  
  // Suggest Edit In-Table State
  const [isSuggestModalOpen, setIsSuggestModalOpen] = useState(false);
  const [suggestSection, setSuggestSection] = useState('cashback_spending');
  const [tableEdits, setTableEdits] = useState({}); // { [section_key]: rows_array }
  const [suggestReason, setSuggestReason] = useState('');
  const [submittingEdit, setSubmittingEdit] = useState(false);
  const [votingEditId, setVotingEditId] = useState(null);

  useEffect(() => {
    if (!masterCardId) return;
    loadData();
  }, [masterCardId]);

  async function loadData() {
    setLoading(true);
    try {
      const [rewardsRes, editsRes] = await Promise.all([
        apiClient.request(`/catalog/cards/${masterCardId}/rewards`),
        apiClient.request(`/catalog/cards/${masterCardId}/edits`)
      ]);
      const loadedSections = rewardsRes?.sections || [];
      setSections(loadedSections);
      setEdits(Array.isArray(editsRes) ? editsRes : []);
    } catch (err) {
      console.error('Failed to load card rewards:', err);
    } finally {
      setLoading(false);
    }
  }

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getSectionIcon = (key) => {
    switch (key) {
      case 'cashback_spending': return <Gift size={16} color="var(--accent-purple)" />;
      case 'bogo_offers': return <Film size={16} color="#ff9f43" />;
      case 'lounge_travel': return <Plane size={16} color="var(--accent-cyan)" />;
      case 'lifestyle_perks': return <Sparkles size={16} color="#ee5253" />;
      default: return <Gift size={16} color="var(--accent-purple)" />;
    }
  };

  // Open in-table suggest modal
  const handleOpenSuggestModal = (defaultSectionKey = null) => {
    const editsMap = {};
    sections.forEach(s => {
      editsMap[s.section_key] = JSON.parse(JSON.stringify(s.rows_data || []));
    });
    setTableEdits(editsMap);

    if (defaultSectionKey) {
      setSuggestSection(defaultSectionKey);
    } else {
      const openKey = Object.keys(expandedSections).find(k => expandedSections[k]);
      setSuggestSection(openKey || sections[0]?.section_key || 'cashback_spending');
    }
    setSuggestReason('');
    setIsSuggestModalOpen(true);
  };

  // Modify cell value in table editor
  const handleCellChange = (sectionKey, rowIndex, colKey, value) => {
    setTableEdits(prev => {
      const sectionRows = [...(prev[sectionKey] || [])];
      if (!sectionRows[rowIndex]) return prev;
      sectionRows[rowIndex] = {
        ...sectionRows[rowIndex],
        [colKey]: value
      };
      return {
        ...prev,
        [sectionKey]: sectionRows
      };
    });
  };

  // Add new empty row to current table
  const handleAddRow = (sectionKey) => {
    const targetSection = sections.find(s => s.section_key === sectionKey);
    const cols = targetSection?.columns_schema || [];
    const newRow = {};
    cols.forEach(col => {
      newRow[col.key] = '';
    });
    setTableEdits(prev => ({
      ...prev,
      [sectionKey]: [...(prev[sectionKey] || []), newRow]
    }));
  };

  // Remove row from current table
  const handleDeleteRow = (sectionKey, rowIndex) => {
    setTableEdits(prev => {
      const sectionRows = (prev[sectionKey] || []).filter((_, idx) => idx !== rowIndex);
      return {
        ...prev,
        [sectionKey]: sectionRows
      };
    });
  };

  // Reset section table back to active catalog state
  const handleResetSection = (sectionKey) => {
    const targetSection = sections.find(s => s.section_key === sectionKey);
    setTableEdits(prev => ({
      ...prev,
      [sectionKey]: JSON.parse(JSON.stringify(targetSection?.rows_data || []))
    }));
    showToast('Section table reset to original catalog baseline', 'info');
  };

  // Submit in-table proposed edit
  async function handleSubmitSuggestion(e) {
    e.preventDefault();
    const currentRows = tableEdits[suggestSection] || [];
    if (currentRows.length === 0) {
      showToast('The table cannot be completely empty. Add at least one perk row.', 'error');
      return;
    }

    setSubmittingEdit(true);
    try {
      const targetSec = sections.find(s => s.section_key === suggestSection);
      const res = await apiClient.request(`/catalog/cards/${masterCardId}/suggest-edit`, 'POST', {
        section_key: suggestSection,
        edit_type: 'VALUE_CHANGE',
        proposed_rows: currentRows,
        reason: suggestReason || `Proposed table edit for ${targetSec?.section_title || suggestSection}`
      });

      showToast(res.message || 'Table edit suggestion submitted for community review!', 'success');
      setIsSuggestModalOpen(false);
      setSuggestReason('');
      await loadData();
    } catch (err) {
      showToast(err.message || 'Failed to submit proposal', 'error');
    } finally {
      setSubmittingEdit(false);
    }
  }

  // Upvote edit
  async function handleVote(editId) {
    if (!isCardholder) {
      showToast('Only verified cardholders can vote on reward proposals.', 'error');
      return;
    }

    setVotingEditId(editId);
    try {
      const res = await apiClient.request(`/catalog/edits/${editId}/vote`, 'POST', {});
      showToast(res.message, 'success');
      await loadData();
    } catch (err) {
      showToast(err.message || 'Failed to vote', 'error');
    } finally {
      setVotingEditId(null);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>
        <Sparkles size={20} className="animate-spin" color="var(--accent-purple)" style={{ margin: '0 auto 8px' }} />
        <span style={{ fontSize: '12px' }}>Loading rewards matrix...</span>
      </div>
    );
  }

  const pendingEdits = edits.filter(e => e.status === 'PENDING');
  const currentEditingSection = sections.find(s => s.section_key === suggestSection) || sections[0];
  const editingCols = currentEditingSection?.columns_schema || [];
  const editingRows = tableEdits[suggestSection] || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {/* Sub-Header & Controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', gap: '6px', background: 'rgba(255, 255, 255, 0.04)', padding: '3px', borderRadius: '10px' }}>
          <button
            type="button"
            onClick={() => setActiveTab('tables')}
            style={{
              padding: '6px 12px',
              borderRadius: '8px',
              border: 'none',
              background: activeTab === 'tables' ? 'var(--accent-purple)' : 'transparent',
              color: activeTab === 'tables' ? 'white' : 'var(--text-muted)',
              fontSize: '11px',
              fontWeight: '700',
              cursor: 'pointer'
            }}
          >
            🎁 Rewards Breakdown
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('proposals')}
            style={{
              padding: '6px 12px',
              borderRadius: '8px',
              border: 'none',
              background: activeTab === 'proposals' ? 'var(--accent-purple)' : 'transparent',
              color: activeTab === 'proposals' ? 'white' : 'var(--text-muted)',
              fontSize: '11px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            <span>Proposed Changes</span>
            {pendingEdits.length > 0 && (
              <span style={{ fontSize: '9px', background: 'rgba(255, 255, 255, 0.25)', padding: '1px 5px', borderRadius: '10px', color: 'white' }}>
                {pendingEdits.length}
              </span>
            )}
          </button>
        </div>

        <button
          type="button"
          onClick={() => handleOpenSuggestModal()}
          style={{
            padding: '6px 12px',
            borderRadius: '8px',
            background: 'rgba(165,94,234,0.15)',
            border: '1px solid rgba(165,94,234,0.35)',
            color: 'var(--accent-purple)',
            fontSize: '11px',
            cursor: 'pointer',
            fontWeight: '700',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px'
          }}
        >
          <Edit3 size={13} /> Suggest Edit
        </button>
      </div>

      {/* VIEW 1: REWARD ACCORDION TABLES */}
      {activeTab === 'tables' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {sections.length === 0 ? (
            <div style={{ padding: '30px 16px', textAlign: 'center', color: 'var(--text-muted)', background: 'rgba(255,255,255,0.02)', borderRadius: '12px' }}>
              <Gift size={28} style={{ opacity: 0.4, margin: '0 auto 8px' }} />
              <p style={{ margin: '0 0 6px', fontSize: '13px', color: 'var(--text-main)' }}>No Reward Data Yet</p>
              <p style={{ margin: 0, fontSize: '11px' }}>Be the first to suggest cashback rates and perks for this card!</p>
            </div>
          ) : (
            sections.map(section => {
              const isExpanded = !!expandedSections[section.section_key];
              const cols = section.columns_schema || [];
              const rows = section.rows_data || [];

              return (
                <div
                  key={section.section_key}
                  style={{
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    background: 'rgba(255, 255, 255, 0.02)',
                    overflow: 'hidden'
                  }}
                >
                  {/* Section Accordion Trigger */}
                  <div
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      background: 'transparent',
                      boxSizing: 'border-box'
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => toggleSection(section.section_key)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        color: 'var(--text-main)',
                        textAlign: 'left',
                        flex: 1,
                        padding: 0
                      }}
                    >
                      {getSectionIcon(section.section_key)}
                      <span style={{ fontSize: '13px', fontWeight: '700' }}>
                        {section.section_title}
                      </span>
                      <span style={{ fontSize: '10px', color: 'var(--text-muted)', background: 'rgba(255,255,255,0.05)', padding: '1px 6px', borderRadius: '4px' }}>
                        {rows.length} {rows.length === 1 ? 'item' : 'items'}
                      </span>
                    </button>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenSuggestModal(section.section_key);
                        }}
                        style={{
                          padding: '3px 8px',
                          borderRadius: '6px',
                          background: 'rgba(165,94,234,0.1)',
                          border: '1px solid rgba(165,94,234,0.25)',
                          color: 'var(--accent-purple)',
                          fontSize: '10.5px',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '3px'
                        }}
                        title="Edit this table"
                      >
                        <Edit3 size={11} /> Edit Table
                      </button>

                      <button
                        type="button"
                        onClick={() => toggleSection(section.section_key)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: 'var(--text-muted)',
                          fontSize: '11px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: 0
                        }}
                      >
                        <span>{isExpanded ? 'Collapse' : 'Expand'}</span>
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                    </div>
                  </div>

                  {/* Table Content */}
                  {isExpanded && (
                    <div style={{
                      padding: '0 12px 12px',
                      overflowX: 'auto',
                      borderTop: '1px solid rgba(255, 255, 255, 0.04)',
                      background: 'rgba(0, 0, 0, 0.15)'
                    }}>
                      {rows.length === 0 ? (
                        <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '11px' }}>
                          No specific entries listed for this category. Click "Edit Table" to add perks.
                        </div>
                      ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', marginTop: '8px' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', color: 'var(--text-muted)', textAlign: 'left' }}>
                              {cols.map(col => (
                                <th key={col.key} style={{ padding: '8px 10px', fontWeight: '700', textTransform: 'uppercase', fontSize: '10px' }}>
                                  {col.label}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((row, idx) => (
                              <tr key={idx} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.03)' }}>
                                {cols.map(col => {
                                  const cellVal = row[col.key];
                                  const isRateCol = col.key === 'rate' || col.label?.toLowerCase().includes('%') || col.label?.toLowerCase().includes('cashback');
                                  return (
                                    <td key={col.key} style={{ padding: '8px 10px', color: isRateCol ? 'var(--accent-purple)' : 'var(--text-main)', fontWeight: isRateCol ? '800' : '400' }}>
                                      {cellVal !== undefined && cellVal !== null && cellVal !== '' ? String(cellVal) : '—'}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* VIEW 2: PROPOSED CHANGES / CROWDSOURCED EDITS */}
      {activeTab === 'proposals' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {edits.length === 0 ? (
            <div style={{ padding: '30px 16px', textAlign: 'center', color: 'var(--text-muted)', background: 'rgba(255,255,255,0.02)', borderRadius: '12px' }}>
              <Clock size={28} style={{ opacity: 0.4, margin: '0 auto 8px' }} />
              <p style={{ margin: '0 0 6px', fontSize: '13px', color: 'var(--text-main)' }}>No Proposals Yet</p>
              <p style={{ margin: 0, fontSize: '11px' }}>Proposals reviewed and verified by cardholders are published to the catalog.</p>
            </div>
          ) : (
            edits.map(edit => {
              const diff = edit.proposed_diff || {};
              const proposedRows = diff.rows || [];
              const isPending = edit.status === 'PENDING';
              const isApproved = edit.status === 'APPROVED';

              return (
                <div
                  key={edit.edit_id}
                  style={{
                    padding: '14px',
                    borderRadius: '12px',
                    border: '1px solid var(--border-glass)',
                    background: 'rgba(255, 255, 255, 0.02)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-main)' }}>
                          {edit.section_title || edit.section_key}
                        </span>
                        <span style={{
                          fontSize: '10px',
                          fontWeight: '700',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          background: isApproved ? 'rgba(46,213,115,0.15)' : 'rgba(255,159,67,0.15)',
                          color: isApproved ? 'var(--color-inflow)' : '#ff9f43'
                        }}>
                          {edit.status}
                        </span>
                      </div>
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginTop: '2px' }}>
                        {edit.reason || 'Community proposed table update'}
                      </span>
                    </div>

                    {/* Upvote Button & Vote Counter (Neutralized wording, no specific threshold mentioned) */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600' }}>
                        {edit.upvotes_count} {edit.upvotes_count === 1 ? 'cardholder vote' : 'cardholder votes'}
                      </span>
                      {isPending && (
                        <button
                          type="button"
                          onClick={() => handleVote(edit.edit_id)}
                          disabled={edit.has_voted || votingEditId === edit.edit_id}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '8px',
                            background: edit.has_voted ? 'rgba(46,213,115,0.15)' : 'rgba(165,94,234,0.2)',
                            border: edit.has_voted ? '1px solid rgba(46,213,115,0.3)' : '1px solid rgba(165,94,234,0.4)',
                            color: edit.has_voted ? 'var(--color-inflow)' : 'var(--accent-purple)',
                            fontSize: '11px',
                            cursor: edit.has_voted ? 'default' : 'pointer',
                            fontWeight: '700',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            opacity: (edit.has_voted || votingEditId === edit.edit_id) ? 0.7 : 1
                          }}
                        >
                          <ThumbsUp size={12} />
                          {edit.has_voted ? 'Voted' : 'Upvote'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Summary of proposed changes */}
                  {proposedRows.length > 0 && (
                    <div style={{ padding: '8px 10px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', fontSize: '11px', color: 'var(--text-muted)' }}>
                      Proposed table contains {proposedRows.length} entries.
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* MODAL: IN-TABLE SUGGEST REWARD EDIT */}
      {isSuggestModalOpen && (
        <div className="modal-backdrop modal-backdrop-blur" style={{ zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <form
            onSubmit={handleSubmitSuggestion}
            className="glass-panel modal-spring-enter"
            style={{
              width: '95%',
              maxWidth: '900px',
              height: '86vh',
              maxHeight: '740px',
              padding: '24px',
              borderRadius: '20px',
              background: 'var(--bg-secondary, #141724)',
              border: '1px solid var(--border-glass)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6)',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              boxSizing: 'border-box',
              overflow: 'hidden'
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--text-main)', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Edit3 size={18} color="var(--accent-purple)" />
                  Suggest Reward Table Edits
                </h3>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                  {cardName} • Direct In-Table Editor
                </span>
              </div>
              <button
                type="button"
                onClick={() => setIsSuggestModalOpen(false)}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '4px' }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Cardholder notice (Neutral wording, no vote number mentioned) */}
            <div style={{
              padding: '10px 14px',
              borderRadius: '10px',
              background: isCardholder ? 'rgba(0,245,212,0.08)' : 'rgba(255,159,67,0.08)',
              border: isCardholder ? '1px solid rgba(0,245,212,0.25)' : '1px solid rgba(255,159,67,0.25)',
              fontSize: '11.5px',
              color: isCardholder ? 'var(--accent-cyan)' : '#ff9f43',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <ShieldCheck size={16} />
              <span>
                {isCardholder 
                  ? '🛡️ Verified Cardholder: Your proposal includes your verified cardholder endorsement.' 
                  : 'Proposals are reviewed and verified by cardholders before publishing.'}
              </span>
            </div>

            {/* Section Switcher Tabs */}
            <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '2px' }}>
              {sections.map(sec => {
                const isActive = sec.section_key === suggestSection;
                return (
                  <button
                    key={sec.section_key}
                    type="button"
                    onClick={() => setSuggestSection(sec.section_key)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '8px',
                      border: isActive ? '1px solid var(--accent-purple)' : '1px solid rgba(255, 255, 255, 0.08)',
                      background: isActive ? 'rgba(165,94,234,0.2)' : 'rgba(255, 255, 255, 0.03)',
                      color: isActive ? 'var(--accent-purple)' : 'var(--text-muted)',
                      fontSize: '11.5px',
                      fontWeight: isActive ? '700' : '500',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    {getSectionIcon(sec.section_key)}
                    {sec.section_title}
                  </button>
                );
              })}
            </div>

            {/* Interactive In-Table Editor Body */}
            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto', border: '1px solid var(--border-glass)', borderRadius: '12px', background: 'rgba(0, 0, 0, 0.2)' }}>
              {editingRows.length === 0 ? (
                <div style={{ padding: '36px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
                  No entries currently in this table. Click "+ Add New Row" below to insert perks.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11.5px' }}>
                  <thead>
                    <tr style={{ background: 'rgba(255, 255, 255, 0.04)', borderBottom: '1px solid var(--border-glass)', textAlign: 'left', color: 'var(--text-muted)' }}>
                      <th style={{ padding: '10px 12px', width: '36px', textAlign: 'center' }}>#</th>
                      {editingCols.map(col => (
                        <th key={col.key} style={{ padding: '10px 12px', fontWeight: '700', fontSize: '10.5px', textTransform: 'uppercase' }}>
                          {col.label}
                        </th>
                      ))}
                      <th style={{ padding: '10px 12px', width: '50px', textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editingRows.map((row, rowIdx) => (
                      <tr key={rowIdx} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.04)' }}>
                        <td style={{ padding: '8px 10px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '10.5px' }}>
                          {rowIdx + 1}
                        </td>
                        {editingCols.map(col => (
                          <td key={col.key} style={{ padding: '6px 8px' }}>
                            <input
                              type="text"
                              value={row[col.key] !== undefined && row[col.key] !== null ? String(row[col.key]) : ''}
                              onChange={e => handleCellChange(suggestSection, rowIdx, col.key, e.target.value)}
                              placeholder={`Enter ${col.label}...`}
                              style={{
                                width: '100%',
                                minWidth: '120px',
                                padding: '8px 10px',
                                borderRadius: '6px',
                                background: 'rgba(255, 255, 255, 0.05)',
                                border: '1px solid rgba(255, 255, 255, 0.08)',
                                color: 'var(--text-main)',
                                fontSize: '12px',
                                boxSizing: 'border-box'
                              }}
                            />
                          </td>
                        ))}
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={() => handleDeleteRow(suggestSection, rowIdx)}
                            title="Remove row"
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: 'var(--color-outflow)',
                              cursor: 'pointer',
                              padding: '6px',
                              borderRadius: '6px'
                            }}
                          >
                            <Trash2 size={15} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Table Utility Buttons: Add Row & Reset */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <button
                type="button"
                onClick={() => handleAddRow(suggestSection)}
                className="btn-secondary"
                style={{
                  padding: '7px 14px',
                  borderRadius: '8px',
                  fontSize: '11.5px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Plus size={14} color="var(--accent-purple)" />
                Add New Row
              </button>

              <button
                type="button"
                onClick={() => handleResetSection(suggestSection)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-muted)',
                  fontSize: '11px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <RotateCcw size={12} />
                Reset to Original
              </button>
            </div>

            {/* Reason / Source Note Input */}
            <div>
              <label style={{ fontSize: '11.5px', fontWeight: '700', display: 'block', marginBottom: '4px', color: 'var(--text-muted)' }}>
                Reason for edit / Source (Optional)
              </label>
              <input
                type="text"
                placeholder="e.g. Bank Schedule of Charges March 2026, confirmed updated cashback rates"
                value={suggestReason}
                onChange={e => setSuggestReason(e.target.value)}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: '8px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid var(--border-glass)',
                  color: 'var(--text-main)',
                  fontSize: '12px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Modal Bottom Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '4px', borderTop: '1px solid var(--border-glass)' }}>
              <button
                type="button"
                onClick={() => setIsSuggestModalOpen(false)}
                className="btn-secondary"
                style={{ padding: '10px 18px', borderRadius: '10px', fontSize: '12px' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submittingEdit}
                className="btn-primary"
                style={{
                  padding: '10px 22px',
                  borderRadius: '10px',
                  fontSize: '12px',
                  fontWeight: '700',
                  opacity: submittingEdit ? 0.6 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {submittingEdit ? 'Submitting Table...' : 'Submit Proposed Edits'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
